/**
 * Fetch Tracker Module
 * 
 * This module provides functionality to track and prevent excessive fetching
 * by maintaining a cache of recently fetched data with timestamps.
 */

// Map to store fetched requests with timestamps
const fetchedRequests = new Map();

// Default time gap between fetches (15 minutes)
let DEFAULT_TIME_GAP = 15 * 60 * 1000;

/**
 * Checks if a request should be allowed to fetch based on time since last fetch
 * 
 * @param {string} event - The event name
 * @param {object} data - The data payload
 * @param {number} timeGap - Minimum time in milliseconds between fetches (default: 15 minutes)
 * @returns {boolean} - True if fetching should be prevented, false if fetching is allowed
 */
export const checkForFetching = (event, data, timeGap = DEFAULT_TIME_GAP) => {
  const currentTime = Date.now();
  const requestKey = JSON.stringify({ event, data });
  
  // Clean up old entries
  cleanupOldEntries(currentTime, timeGap);
  
  // Check if this request was recently fetched
  const lastFetchTime = fetchedRequests.get(requestKey);
  
  if (!lastFetchTime || (currentTime - lastFetchTime) > timeGap) {
    // Update the timestamp for this request
    fetchedRequests.set(requestKey, currentTime);
    return false; // Allow fetching
  }
  
  return true; // Too soon to fetch again
};

/**
 * Cleans up old entries from the fetched requests map
 * 
 * @param {number} currentTime - Current timestamp
 * @param {number} timeGap - Time gap threshold
 */
const cleanupOldEntries = (currentTime, timeGap) => {
  for (const [key, timestamp] of fetchedRequests.entries()) {
    if (currentTime - timestamp > timeGap) {
      fetchedRequests.delete(key);
    }
  }
};

/**
 * Clears all tracked fetches
 */
export const clearFetchTracker = () => {
  fetchedRequests.clear();
};

/**
 * Gets the current number of tracked fetches
 * 
 * @returns {number} - The number of tracked fetches
 */
export const getTrackedFetchCount = () => {
  return fetchedRequests.size;
};

/**
 * Sets a custom time gap for fetch tracking
 * 
 * @param {number} timeGap - Time in milliseconds
 */
export const setTimeGap = (timeGap) => {
  if (typeof timeGap === 'number' && timeGap > 0) {
    DEFAULT_TIME_GAP = timeGap;
  }
}; 